import React from 'react';
import { PlayFab } from './services/PlayFab';
import { Account } from './services/Account';
import { Navigate } from 'react-router-dom';
import './StatsPage.css';
import { RangeSelector } from './components/RangeSelector';

const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const SUITS = [
  { symbol: '♣', name: 'c', color: 'black' },
  { symbol: '♥', name: 'h', color: 'red' },
  { symbol: '♦', name: 'd', color: 'red' },
  { symbol: '♠', name: 's', color: 'black' },
];

interface HandEquity {
  equity: number;
}

interface Player {
  id: number;
  name: string;
  cards: string[];
  range?: Set<string>;
  useRange?: boolean;
}

interface StatsPageState {
  players: Player[];
  showCardPicker: boolean;
  showRangeSelector: boolean;
  rangePlayerId: number | null;
  cardPickerTarget: {
    playerId: number;
    cardIndex: number;
  } | null;
  equities: HandEquity[];
  loading: boolean;
  error: string | null;
}

// Update the Card component to remove buddy-container
const Card: React.FC<{ card: string; onClick?: () => void }> = ({ card, onClick }) => {
  if (!card) return (
    <div className="card" onClick={onClick} style={{ cursor: 'pointer' }} />
  );

  const rank = card[0];
  const suit = card[1];
  const color = suit === 'h' || suit === 'd' ? 'red' : 'black';
  const suitSymbol = {
    'c': '♣',
    'h': '♥',
    'd': '♦',
    's': '♠'
  }[suit];

  return (
    <div className="card filled" onClick={onClick} style={{ cursor: 'pointer' }}>
      <span style={{ color, fontSize: '20px' }}>{rank}</span>
      <span style={{ color, fontSize: '24px' }}>{suitSymbol}</span>
    </div>
  );
};

// Reuse and modify the CardPicker from BuddyPage
const CardPicker: React.FC<{
  show: boolean;
  onSelect: (card: string) => void;
  onClose: () => void;
  usedCards: string[];
}> = ({ show, onSelect, onClose, usedCards }) => {
  if (!show) return null;

  const isCardUsed = (card: string): boolean => {
    return usedCards.includes(card);
  };

  return (
    <div className="card-picker-overlay">
      <div className="card-picker-modal">
        <h2 className="card-picker-title">Select a Card</h2>
        
        <div className="card-picker-grid">
          <div className="card-picker-ranks">
            {RANKS.map(rank => (
              <div key={rank} className="rank-header">{rank}</div>
            ))}
          </div>

          {SUITS.map(suit => (
            <div key={suit.name} className="suit-row">
              {RANKS.map(rank => {
                const card = `${rank}${suit.name}`;
                const isUsed = isCardUsed(card);
                return (
                  <button
                    key={card}
                    className={`card-button ${isUsed ? 'used' : ''}`}
                    style={{ color: suit.color }}
                    disabled={isUsed}
                    onClick={() => !isUsed && onSelect(card)}
                  >
                    <span className="card-text">{rank}{suit.symbol}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <button className="close-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

// Add these helper functions at the top level
const generateDeck = (usedCards: string[]): string[] => {
  const deck: string[] = [];
  for (const rank of RANKS) {
    for (const suit of SUITS) {
      const card = `${rank}${suit.name}`;
      if (!usedCards.includes(card)) {
        deck.push(card);
      }
    }
  }
  return deck;
};

// Add these helper functions
const RANK_VALUES: { [key: string]: number } = {
  'A': 14, 'K': 13, 'Q': 12, 'J': 11, 'T': 10,
  '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2
};

const getHandRank = (cards: string[]): number => {
  const ranks = cards.map(card => card[0]);
  const suits = cards.map(card => card[1]);
  const rankValues = ranks.map(r => RANK_VALUES[r]).sort((a, b) => b - a);
  
  // Check for flush
  const isFlush = suits.every(suit => suit === suits[0]);
  
  // Check for straight
  let isStraight = false;
  for (let i = 0; i < rankValues.length - 4; i++) {
    if (
      rankValues[i] === rankValues[i + 1] + 1 &&
      rankValues[i + 1] === rankValues[i + 2] + 1 &&
      rankValues[i + 2] === rankValues[i + 3] + 1 &&
      rankValues[i + 3] === rankValues[i + 4] + 1
    ) {
      isStraight = true;
      break;
    }
  }
  // Special case for Ace-low straight (A,2,3,4,5)
  if (rankValues[0] === 14 && rankValues[1] === 5 && rankValues[2] === 4 && 
      rankValues[3] === 3 && rankValues[4] === 2) {
    isStraight = true;
  }

  // Check for pairs, trips, quads
  const rankCounts: { [key: number]: number } = {};
  rankValues.forEach(rank => {
    rankCounts[rank] = (rankCounts[rank] || 0) + 1;
  });
  
  const counts = Object.values(rankCounts).sort((a, b) => b - a);
  const pairs = counts.filter(count => count === 2).length;
  const hasThreeOfAKind = counts.includes(3);
  const hasFourOfAKind = counts.includes(4);
  
  // Calculate hand value with kickers
  let baseScore = 0;
  let kickers = rankValues;

  // Straight flush
  if (isFlush && isStraight) return 8000000 + Math.max(...rankValues);
  
  // Four of a kind
  if (hasFourOfAKind) {
    const quadRank = Number(Object.keys(rankCounts).find(rank => rankCounts[Number(rank)] === 4));
    const kicker = rankValues.find(r => r !== quadRank) || 0;
    return 7000000 + (quadRank * 100) + kicker;
  }
  
  // Full house
  if (hasThreeOfAKind && pairs === 1) {
    const tripRank = Number(Object.keys(rankCounts).find(rank => rankCounts[Number(rank)] === 3));
    const pairRank = Number(Object.keys(rankCounts).find(rank => rankCounts[Number(rank)] === 2));
    return 6000000 + (tripRank * 100) + pairRank;
  }
  
  // Flush
  if (isFlush) {
    return 5000000 + rankValues.reduce((acc, rank, i) => acc + (rank * Math.pow(100, 4-i)), 0);
  }
  
  // Straight
  if (isStraight) {
    return 4000000 + Math.max(...rankValues);
  }
  
  // Three of a kind
  if (hasThreeOfAKind) {
    const tripRank = Number(Object.keys(rankCounts).find(rank => rankCounts[Number(rank)] === 3));
    const kickers = rankValues.filter(r => r !== tripRank).slice(0, 2);
    return 3000000 + (tripRank * 10000) + (kickers[0] * 100) + (kickers[1] || 0);
  }
  
  // Two pair
  if (pairs === 2) {
    const pairRanks = Object.keys(rankCounts)
      .filter(rank => rankCounts[Number(rank)] === 2)
      .map(Number)
      .sort((a, b) => b - a);
    const kicker = rankValues.find(r => !pairRanks.includes(r)) || 0;
    return 2000000 + (pairRanks[0] * 1000) + (pairRanks[1] * 100) + kicker;
  }
  
  // One pair
  if (pairs === 1) {
    const pairRank = Number(Object.keys(rankCounts).find(rank => rankCounts[Number(rank)] === 2));
    const kickers = rankValues.filter(r => r !== pairRank).slice(0, 3);
    return 1000000 + (pairRank * 10000) + 
           (kickers[0] * 100) + 
           (kickers[1] * 10) + 
           (kickers[2] || 0);
  }
  
  // High card
  return rankValues.reduce((acc, rank, i) => acc + (rank * Math.pow(100, 4-i)), 0);
};

const simulateHand = (playerHands: string[][], communityCards: string[], deck: string[]): number[] => {
  // Complete the board if needed
  const remainingCards = 5 - communityCards.length;
  const simulatedBoard = [...communityCards];
  const simDeck = [...deck];
  
  for (let i = 0; i < remainingCards; i++) {
    const randomIndex = Math.floor(Math.random() * simDeck.length);
    simulatedBoard.push(simDeck[randomIndex]);
    simDeck.splice(randomIndex, 1);
  }
  
  // Evaluate each player's hand
  const scores = playerHands.map(hand => {
    const allCards = [...hand, ...simulatedBoard];
    return getHandRank(allCards);
  });
  
  // Find winners (can be multiple in case of tie)
  const maxScore = Math.max(...scores);
  return scores.map(score => score === maxScore ? 1 : 0);
};

// Add this helper function
const generateHandFromNotation = (notation: string, usedCards: string[]): string[] | null => {
  const ranks = notation.split('').filter(c => 'AKQJT98765432'.includes(c));
  const suited = notation.endsWith('s');
  
  if (ranks.length !== 2) return null;
  
  const [rank1, rank2] = ranks;
  const availableSuits = ['h', 'd', 'c', 's'].filter(suit => 
    !usedCards.includes(rank1 + suit) && !usedCards.includes(rank2 + suit)
  );

  if (availableSuits.length === 0) return null;

  if (rank1 === rank2) { // Pair
    if (availableSuits.length < 2) return null;
    const [suit1, suit2] = availableSuits.slice(0, 2);
    return [rank1 + suit1, rank1 + suit2];
  }

  if (suited) {
    const suit = availableSuits[0];
    return [rank1 + suit, rank2 + suit];
  } else {
    // Offsuit - need different suits
    if (availableSuits.length < 2) return null;
    return [rank1 + availableSuits[0], rank2 + availableSuits[1]];
  }
};

class StatsPage extends React.Component<{}, StatsPageState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      players: [
        { id: 0, name: 'Hero', cards: ['', ''] },
        { id: 1, name: 'Villain 1', cards: ['', ''] }
      ],
      showCardPicker: false,
      showRangeSelector: false,
      rangePlayerId: null,
      cardPickerTarget: null,
      equities: [],
      loading: false,
      error: null
    };
  }

  addVillain = () => {
    this.setState(prevState => ({
      players: [
        ...prevState.players,
        {
          id: prevState.players.length,
          name: `Villain ${prevState.players.length}`,
          cards: ['', '']
        }
      ]
    }));
  };

  removeVillain = (villainId: number) => {
    if (villainId === 0) return; // Can't remove Hero
    this.setState(prevState => ({
      players: prevState.players.filter(p => p.id !== villainId)
    }));
  };

  handleCardClick = (playerId: number, cardIndex: number) => {
    this.setState({
      showCardPicker: true,
      cardPickerTarget: { playerId, cardIndex }
    });
  };

  handleCardSelect = (card: string) => {
    if (!this.state.cardPickerTarget) return;

    const { playerId, cardIndex } = this.state.cardPickerTarget;
    
    this.setState(prevState => ({
      players: prevState.players.map(player => 
        player.id === playerId
          ? {
              ...player,
              cards: player.cards.map((c, i) => 
                i === cardIndex ? card : c
              )
            }
          : player
      ),
      showCardPicker: false,
      cardPickerTarget: null
    }));
  };

  calculateEquity = () => {
    this.setState({ loading: true, error: null });

    try {
      // Validate hands first
      const invalidPlayer = this.state.players.find(p => {
        if (p.useRange) {
          return !p.range || p.range.size === 0;
        }
        return !p.cards[0] || !p.cards[1];
      });

      if (invalidPlayer) {
        throw new Error(`${invalidPlayer.name} has incomplete cards/range`);
      }

      const numSimulations = 10000;
      const wins = this.state.players.map(() => 0);
      
      for (let i = 0; i < numSimulations; i++) {
        const usedCardsInSim = [...this.getAllUsedCards()];
        
        // Generate hands for this simulation
        const playerHands = this.state.players.map(player => {
          if (!player.useRange) {
            return player.cards;
          }

          const rangeArray = Array.from(player.range || new Set<string>());
          if (rangeArray.length === 0) {
            throw new Error(`${player.name} has no hands in range`);
          }

          // Shuffle range array
          for (let i = rangeArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [rangeArray[i], rangeArray[j]] = [rangeArray[j], rangeArray[i]];
          }

          // Try each hand in the range until we find valid cards
          for (const notation of rangeArray) {
            const cards = generateHandFromNotation(notation, usedCardsInSim);
            if (cards) {
              usedCardsInSim.push(...cards);
              return cards;
            }
          }

          throw new Error(`Could not generate valid cards for ${player.name}'s range`);
        });

        const remainingDeck = generateDeck(usedCardsInSim);
        const results = simulateHand(playerHands, [], remainingDeck);
        
        results.forEach((result, index) => {
          wins[index] += result;
        });
      }

      const equities: HandEquity[] = wins.map(win => ({
        equity: win / numSimulations
      }));

      this.setState({ equities, loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.setState({ 
        error: 'Error calculating equities: ' + errorMessage,
        loading: false 
      });
    }
  };

  getAllUsedCards = (): string[] => {
    return this.state.players.flatMap(p => p.cards.filter(c => c !== ''));
  };

  toggleRangeMode = (playerId: number) => {
    this.setState(prevState => ({
      players: prevState.players.map(player =>
        player.id === playerId ? { ...player, useRange: !player.useRange } : player
      )
    }));
  };

  openRangeSelector = (playerId: number) => {
    this.setState({
      showRangeSelector: true,
      rangePlayerId: playerId
    });
  };

  handleRangeChange = (range: Set<string>) => {
    if (this.state.rangePlayerId === null) return;
    
    this.setState(prevState => ({
      players: prevState.players.map(player =>
        player.id === this.state.rangePlayerId
          ? { ...player, range }
          : player
      )
    }));
  };

  render() {
    if (!Account.isLoggedIn()) {
      return <Navigate to="/" />;
    }
  
    return (
      <div className="page stats-page">
        <h1>Poker Equity Calculator</h1>

        <div className="players-container">
          {this.state.players.map(player => (
            <div key={player.id} className="player-section">
              <div className="player-header">
                <h3>{player.name}</h3>
                {player.id !== 0 && (
                  <>
                    <button
                      onClick={() => this.toggleRangeMode(player.id)}
                      className="range-toggle"
                    >
                      {player.useRange ? 'Range' : 'Exact'}
                    </button>
                    <button 
                      onClick={() => this.removeVillain(player.id)}
                      className="remove-villain"
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
              {player.useRange ? (
                <button
                  className="range-selector-button"
                  onClick={() => this.openRangeSelector(player.id)}
                >
                  {player.range?.size || 0} hands selected
                </button>
              ) : (
                <div className="hole-cards">
                  {player.cards.map((card, index) => (
                    <Card
                      key={index}
                      card={card}
                      onClick={() => this.handleCardClick(player.id, index)}
                    />
                  ))}
                </div>
              )}
              {this.state.equities.length > 0 && (
                <div className="equity">
                  Equity: {(this.state.equities[player.id]?.equity * 100).toFixed(2)}%
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="controls">
          <button 
            onClick={this.addVillain}
            className="add-villain"
            disabled={this.state.players.length >= 9}
          >
            Add Villain
          </button>
          <button 
            onClick={this.calculateEquity}
            className="calculate-equity"
            disabled={this.state.loading}
          >
            Calculate Equity
          </button>
        </div>

        {this.state.loading && (
          <div className="loading">Calculating equities...</div>
        )}

        {this.state.error && (
          <div className="error">{this.state.error}</div>
        )}

        <CardPicker
          show={this.state.showCardPicker}
          onSelect={this.handleCardSelect}
          onClose={() => this.setState({ showCardPicker: false })}
          usedCards={this.getAllUsedCards()}
        />

        <RangeSelector
          isOpen={this.state.showRangeSelector}
          onClose={() => this.setState({ showRangeSelector: false, rangePlayerId: null })}
          onRangeChange={this.handleRangeChange}
        />
      </div>
    );
  }
}

export default StatsPage;